import { Injectable } from '@angular/core';
import { ApiInventoryService } from '@core/services/api/api-inventory.service';
import { ApiClientService } from '@core/services/client/api-client.service';
import { camelCase, snakeCase } from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

interface WorkEntry {
  id: number;
  cat: number[];
  sty: number[];
  the: number[];
  mat: number[];
  per: number[];
  typ: number[];
  cou: number[];
  art: number[];
  iws: number | null;
  arch: boolean;
  prc: number | null;
  hpr: boolean;
  yr: number | null;
  cus: Record<string, number[]>;
}

interface FacetIndex {
  works: WorkEntry[];
  dicts: Record<string, { id: number, name: string, parent_id: number | null }[]>;
  customDicts: Record<string, { name: string, entries: { id: number, name: string, parent_id: number | null }[] }>;
  meta: { client_id: number, lang: string, works_count: number, generated_at: string };
}

/**
 * Aktywne filtry pochodzące z panelu — dokładnie te wartości, które user
 * zaznaczył w checkboxach. Jeżeli grupa jest pominięta lub tablica pusta,
 * traktujemy filtr jako nieaktywny.
 */
interface ActiveFilters {
  categories?: number[];
  styles?: number[];
  themes?: number[];
  materials?: number[];
  periods?: number[];
  types?: number[];
  countries?: number[];
  artists?: number[];
  inventoryStatuses?: number[];
  availability?: number | number[];
  priceFrom?: number;
  priceTo?: number;
  yearFrom?: number;
  yearTo?: number;

  [customKey: string]: number[] | number | undefined;
}

interface IdCount {
  id: number,
  count: number
}

/**
 * Wynik zwracany do frontendu w formacie używanym przez
 * `AdvancedFiltersComponent.getFiltersCounts`.
 */
interface FacetCountsResponse {
  count: number;
  categoriesName: IdCount[];
  stylesName: IdCount[];
  themesName: IdCount[];
  materialsName: IdCount[];
  periodsName: IdCount[];
  typesName: IdCount[];
  countriesName: IdCount[];
  artistsName: IdCount[];
  availabilityName: IdCount[];
  inventoryStatusesName: IdCount[];

  [customKeyName: string]: number | IdCount[];
}

interface M2mKey {
  key: keyof ActiveFilters,
  workKey: keyof WorkEntry,
  respKey: string
}

const M2M_KEYS: M2mKey[] = [
  {key: 'categories', workKey: 'cat', respKey: 'categoriesName'},
  {key: 'styles', workKey: 'sty', respKey: 'stylesName'},
  {key: 'themes', workKey: 'the', respKey: 'themesName'},
  {key: 'materials', workKey: 'mat', respKey: 'materialsName'},
  {key: 'periods', workKey: 'per', respKey: 'periodsName'},
  {key: 'types', workKey: 'typ', respKey: 'typesName'},
  {key: 'countries', workKey: 'cou', respKey: 'countriesName'},
  {key: 'artists', workKey: 'art', respKey: 'artistsName'},
];

@Injectable({providedIn: 'root'})
export class LocalFacetingService {
  private index: FacetIndex | null = null;
  private readonly indexReady$ = new BehaviorSubject<boolean>(false);
  readonly ready$: Observable<boolean> = this.indexReady$.asObservable();

  /**
   * Cache childrenów po parentId dla każdej grupy — używany do rozwijania
   * zaznaczonego rodzica na jego potomków w hierarchii (jednorazowo per
   * indeks). Klucz: `dictKey|parentId`.
   */
  private descendantsCache = new Map<string, number[]>();

  constructor(private readonly apiInventoryService: ApiInventoryService) {
  }

  /**
   * Pobiera indeks z backendu i cache'uje w pamięci na czas życia SPA.
   * Kolejne wywołania zwracają natychmiast z pamięci. sessionStorage jako
   * dodatkowa warstwa — przetrwa F5 w obrębie tej samej karty.
   */
  loadIndex(lang: string): Observable<boolean> {
    if (this.index && this.index.meta.lang === lang) {
      return of(true);
    }
    const cached = this.readFromSession(lang);
    if (cached) {
      this.setIndex(cached);
      return of(true);
    }
    return this.apiInventoryService.getFacetIndex(lang).pipe(
      tap((idx: FacetIndex) => {
        this.setIndex(idx);
        this.writeToSession(lang, idx);
      }),
      map(() => true),
      catchError(err => {

        console.warn('[LocalFacetingService] Nie udało się pobrać indeksu — fallback do backendu', err);
        return of(false);
      }),
    );
  }

  isAvailable(): boolean {
    return this.index !== null;
  }

  supportsCustomFilters(filterNames: string[]): boolean {
    if (!this.index || filterNames.length === 0) {
      return false;
    }

    const availableKeys = new Set(
      Object.keys(this.index.customDicts || {})
        .flatMap(indexKey => this.getCustomFilterAliases(indexKey)),
    );

    return filterNames.every(filterName => availableKeys.has(filterName));
  }

  /**
   * Liczy liczniki dla wszystkich grup filtrów pod zestawem aktywnych
   * wyborów, w formacie identycznym z dotychczasowym endpointem liczników.
   * `base` to ograniczenia listy, np. "tylko dostępne prace"; one zawężają
   * cały indeks i nie są ignorowane przy liczeniu żadnej grupy.
   * Zasada disjunctive faceting: dla każdej grupy liczymy jej wartości
   * pod filtrami POZOSTAŁYCH grup — dzięki czemu user widzi, ile prac
   * miałaby każda wartość, gdyby ją dodatkowo zaznaczył.
   */
  computeFacets(active: ActiveFilters, base: ActiveFilters = {}): FacetCountsResponse {
    if (!this.index) {
      throw new Error('LocalFacetingService: indeks nie jest jeszcze załadowany');
    }
    const works = this.index.works;

    const expanded = this.expandAllHierarchies(active);
    const expandedBase = this.expandAllHierarchies(base);

    // Dla każdej grupy zbieramy licznik w mapie id → count.
    const groupCounts: Record<string, Map<number, number>> = {};
    for (const {respKey} of M2M_KEYS) {
      groupCounts[respKey] = new Map();
    }
    const availabilityCounts = new Map<number, number>();
    const statusesCounts = new Map<number, number>();
    const customCounts: Record<string, Map<number, number>> = {};
    for (const key of Object.keys(this.index.customDicts)) {
      customCounts[this.getCustomResponseKey(key)] = new Map();
    }

    let totalCount = 0;

    for (const w of works) {
      if (!this.workMatchesFilters(w, expandedBase)) {
        continue;
      }

      // Sprawdzamy każdy filtr indywidualnie — flagi "pasuje ignorując grupę X".
      // Optymalizacja: liczymy licznik "ile filtrów oprócz X blokuje pracę",
      // dzięki czemu wystarczy jeden przelot per praca.
      let blockingCount = 0;
      const blockingByGroup: Record<string, boolean> = {};
      // M2M
      for (const {key, workKey, respKey} of M2M_KEYS) {
        const filterValsRaw = expanded[key];
        const filterVals = Array.isArray(filterValsRaw) ? filterValsRaw : undefined;
        if (filterVals && filterVals.length) {
          const workVals = w[workKey] as number[];
          const matches = workVals.some(v => filterVals.includes(v));
          if (!matches) {
            blockingCount++;
            blockingByGroup[respKey] = true;
          }
        }
      }
      // inventory_statuses
      const statusesVals = Array.isArray(expanded.inventoryStatuses) ? expanded.inventoryStatuses : undefined;
      if (statusesVals && statusesVals.length) {
        const matches = w.iws !== null && statusesVals.includes(w.iws);
        if (!matches) {
          blockingCount++;
          blockingByGroup['inventoryStatusesName'] = true;
        }
      }
      // availability według enumu backendu: 1 = dostępne, -1 = archiwalne.
      const availabilityVals = Array.isArray(expanded.availability) ? expanded.availability : undefined;
      if (availabilityVals && availabilityVals.length) {
        const matches = availabilityVals.includes(w.arch ? -1 : 1);
        if (!matches) {
          blockingCount++;
          blockingByGroup['availabilityName'] = true;
        }
      }
      // Cena
      if (typeof active.priceFrom === 'number' || typeof active.priceTo === 'number') {
        const priceOk = w.hpr || this.inRange(w.prc, active.priceFrom, active.priceTo);
        if (!priceOk) {
          blockingCount++;
          blockingByGroup['priceRange'] = true;
        }
      }
      // Rok
      if (typeof active.yearFrom === 'number' || typeof active.yearTo === 'number') {
        const yearOk = this.inRange(w.yr, active.yearFrom, active.yearTo);
        if (!yearOk) {
          blockingCount++;
          blockingByGroup['yearRange'] = true;
        }
      }
      // Custom dictionaries
      for (const customKey of Object.keys(this.index.customDicts)) {
        const filterValsRaw = this.getCustomFilterValues(expanded, customKey);
        const filterVals = Array.isArray(filterValsRaw) ? filterValsRaw : undefined;
        const respKey = this.getCustomResponseKey(customKey);
        if (filterVals && filterVals.length) {
          const workVals = w.cus?.[customKey] || [];
          const matches = workVals.some(v => filterVals.includes(v));
          if (!matches) {
            blockingCount++;
            blockingByGroup[respKey] = true;
          }
        }
      }

      if (blockingCount === 0) {
        totalCount++;
      }

      // Grupa X wchodzi do liczników jeśli praca jest blokowana wyłącznie przez X
      // (lub przez żadną grupę). Wtedy w widoku danej grupy praca „byłaby dostępna".
      const passesExceptGroup = (respKey: string): boolean =>
        blockingCount === 0 || (blockingCount === 1 && blockingByGroup[respKey] === true);

      for (const {workKey, respKey} of M2M_KEYS) {
        if (passesExceptGroup(respKey)) {
          const workVals = w[workKey] as number[];
          for (const id of workVals) {
            groupCounts[respKey].set(id, (groupCounts[respKey].get(id) ?? 0) + 1);
          }
        }
      }
      if (passesExceptGroup('inventoryStatusesName') && w.iws !== null) {
        statusesCounts.set(w.iws, (statusesCounts.get(w.iws) ?? 0) + 1);
      }
      if (passesExceptGroup('availabilityName')) {
        const availKey = w.arch ? -1 : 1;
        availabilityCounts.set(availKey, (availabilityCounts.get(availKey) ?? 0) + 1);
      }
      for (const customKey of Object.keys(this.index.customDicts)) {
        const respKey = this.getCustomResponseKey(customKey);
        if (passesExceptGroup(respKey)) {
          const workVals = w.cus?.[customKey] || [];
          for (const id of workVals) {
            customCounts[respKey].set(id, (customCounts[respKey].get(id) ?? 0) + 1);
          }
        }
      }
    }

    const toArray = (m: Map<number, number>) =>
      Array.from(m.entries()).map(([id, count]) => ({id, count}));

    const response: FacetCountsResponse = {
      count: totalCount,
      categoriesName: toArray(groupCounts['categoriesName']),
      stylesName: toArray(groupCounts['stylesName']),
      themesName: toArray(groupCounts['themesName']),
      materialsName: toArray(groupCounts['materialsName']),
      periodsName: toArray(groupCounts['periodsName']),
      typesName: toArray(groupCounts['typesName']),
      countriesName: toArray(groupCounts['countriesName']),
      artistsName: toArray(groupCounts['artistsName']),
      availabilityName: toArray(availabilityCounts),
      inventoryStatusesName: toArray(statusesCounts),
    };
    for (const [respKey, m] of Object.entries(customCounts)) {
      response[respKey] = toArray(m);
    }
    return response;
  }

  private workMatchesFilters(work: WorkEntry, filters: ActiveFilters): boolean {
    if (!this.index) {
      return true;
    }

    for (const {key, workKey} of M2M_KEYS) {
      const filterValsRaw = filters[key];
      const filterVals = Array.isArray(filterValsRaw) ? filterValsRaw : undefined;
      if (filterVals?.length) {
        const workVals = work[workKey] as number[];
        if (!workVals.some(v => filterVals.includes(v))) {
          return false;
        }
      }
    }

    const statusesVals = Array.isArray(filters.inventoryStatuses) ? filters.inventoryStatuses : undefined;
    if (statusesVals?.length && (work.iws === null || !statusesVals.includes(work.iws))) {
      return false;
    }

    const availabilityVals = Array.isArray(filters.availability) ? filters.availability : undefined;
    if (availabilityVals?.length && !availabilityVals.includes(work.arch ? -1 : 1)) {
      return false;
    }

    if ((typeof filters.priceFrom === 'number' || typeof filters.priceTo === 'number') &&
      !(work.hpr || this.inRange(work.prc, filters.priceFrom, filters.priceTo))) {
      return false;
    }

    if ((typeof filters.yearFrom === 'number' || typeof filters.yearTo === 'number') &&
      !this.inRange(work.yr, filters.yearFrom, filters.yearTo)) {
      return false;
    }

    for (const customKey of Object.keys(this.index.customDicts)) {
      const filterValsRaw = this.getCustomFilterValues(filters, customKey);
      const filterVals = Array.isArray(filterValsRaw) ? filterValsRaw : undefined;
      if (filterVals?.length) {
        const workVals = work.cus?.[customKey] || [];
        if (!workVals.some(v => filterVals.includes(v))) {
          return false;
        }
      }
    }

    return true;
  }

  private inRange(val: number | null, min?: number, max?: number): boolean {
    if (val === null) return false;
    if (typeof min === 'number' && val < min) return false;
    return !(typeof max === 'number' && val > max);
  }

  /**
   * Rozwija zaznaczone wartości hierarchiczne (kategorie, style, tematy,
   * okresy, typy, kraje + custom dicts) o wszystkich ich potomków. Dzięki
   * temu klik na rodzicu zwraca też prace przypisane wyłącznie do dziecka.
   */
  private expandAllHierarchies(active: ActiveFilters): ActiveFilters {
    if (!this.index) return active;
    const expanded: ActiveFilters = {...active};
    const hierarchical = ['categories', 'styles', 'themes', 'periods', 'types', 'countries'] as const;
    for (const key of hierarchical) {
      const vals = active[key];
      if (Array.isArray(vals) && vals.length) {
        expanded[key] = this.expandHierarchy(this.index.dicts[key] || [], vals);
      }
    }
    for (const customKey of Object.keys(this.index.customDicts)) {
      const vals = this.getCustomFilterValues(active, customKey);
      if (Array.isArray(vals) && vals.length) {
        expanded[customKey] = this.expandHierarchy(
          this.index.customDicts[customKey].entries || [],
          vals,
        );
      }
    }
    return expanded;
  }

  private getCustomFilterValues(active: ActiveFilters, indexKey: string): number[] | number | undefined {
    for (const alias of this.getCustomFilterAliases(indexKey)) {
      const value = active[alias];
      if (Array.isArray(value) && value.length) {
        return value;
      }
    }

    return undefined;
  }

  private getCustomResponseKey(indexKey: string): string {
    return `${this.getCustomUiKey(indexKey)}Name`;
  }

  private getCustomUiKey(indexKey: string): string {
    const camelKey = camelCase(indexKey);

    if (camelKey.startsWith('customCustom')) {
      return `custom${camelKey.slice('customCustom'.length)}`;
    }

    return camelKey;
  }

  private getCustomFilterAliases(indexKey: string): string[] {
    const aliases = new Set<string>();
    const camelKey = camelCase(indexKey);
    const uiKey = this.getCustomUiKey(indexKey);

    aliases.add(indexKey);
    aliases.add(camelKey);
    aliases.add(uiKey);
    aliases.add(snakeCase(indexKey));
    aliases.add(snakeCase(uiKey));

    return Array.from(aliases);
  }

  private expandHierarchy(
    entries: { id: number, parent_id: number | null }[],
    selectedIds: number[],
  ): number[] {
    const childrenByParent = new Map<number, number[]>();
    for (const e of entries) {
      if (e.parent_id !== null) {
        const arr = childrenByParent.get(e.parent_id) ?? [];
        arr.push(e.id);
        childrenByParent.set(e.parent_id, arr);
      }
    }
    const result = new Set<number>(selectedIds);
    const stack = [...selectedIds];
    while (stack.length) {
      const cur = stack.pop() as number;
      const children = childrenByParent.get(cur);
      if (!children) continue;
      for (const c of children) {
        if (!result.has(c)) {
          result.add(c);
          stack.push(c);
        }
      }
    }
    return Array.from(result);
  }

  private setIndex(idx: FacetIndex): void {
    this.index = idx;
    this.descendantsCache.clear();
    this.indexReady$.next(true);
  }

  private sessionKey(lang: string): string {
    const clientId = ApiClientService.CACHED_CLIENT?.id ?? '0';
    return `facet-index:${clientId}:${lang}`;
  }

  private readFromSession(lang: string): FacetIndex | null {
    try {
      const raw = sessionStorage.getItem(this.sessionKey(lang));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // TTL 5 minut — po tym czasie wymuszamy odświeżenie z backendu.
      if (Date.now() - parsed.__savedAt > 5 * 60 * 1000) {
        sessionStorage.removeItem(this.sessionKey(lang));
        return null;
      }
      return parsed as FacetIndex;
    } catch {
      return null;
    }
  }

  private writeToSession(lang: string, idx: FacetIndex): void {
    try {
      const payload = {...idx, __savedAt: Date.now()};
      sessionStorage.setItem(this.sessionKey(lang), JSON.stringify(payload));
    } catch {
      // np. QuotaExceededError przy bardzo dużych indeksach — nie fatalne.
    }
  }
}
